import { getCurrency } from '@common/utils/currency'
import { type ColumnDef } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'

import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'
import MailOutlineIcon from '@/assets/icons/icon-mail-outline.svg'
import { Button } from '@/components/ui/button.tsx'
import { getFormattedDateRange } from '@/components/ui/export'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { formatNumbersForSheet, getPercentValue } from '@/utils/helpers'

import { usePpvTracking } from '../../hooks/usePpvTracking'
import type { PpvMediaSet } from '../../types'
import { getLastSentAgo } from '../../utils'
import { PpvTrackingQueryPanel } from '../PpvTrackingQueryPanel'

interface PpvCategoryTableProps {
  mediaSets: PpvMediaSet[]
  setSearch: (query: string) => void
  showCents?: boolean
}

const tableColumnTitles = {
  ppvNames: 'PPV Names',
  sent: 'Sent',
  bought: 'Bought',
  purchaseRate: 'Purchase Rate',
  netRevenue: 'Net Revenue',
  averagePrice: 'Avg. Net Price',
}

export const PpvCategoryMediaSetsTable = ({
  mediaSets,
  setSearch,
  showCents,
}: PpvCategoryTableProps) => {
  const navigate = useNavigate()
  const { date } = usePpvTracking()

  const handleButtonClick = (index: number) => () => {
    navigate(`messages/${index}`)
  }

  const columns: ColumnDef<PpvMediaSet>[] = [
    {
      header: tableColumnTitles.ppvNames,
      accessorKey: 'highlighted.content',
      enableSorting: true,
      sortingFn: 'text',
      cell: (props) => {
        const {
          highlighted: { mediaLabel, content },
          lastSent,
        } = props.row.original

        return (
          <div className="flex items-center gap-4">
            <MailOutlineIcon className="fill-secondary-500 dark:fill-white" />
            <div>
              <div className="... w-[180px] truncate xl:w-[250px]">
                {content && content.length > 0 ? content : 'No text'}
              </div>
              <div className="... w-[180px] truncate text-xs leading-5 text-secondary-300 xl:w-[250px]">
                {mediaLabel} • {getLastSentAgo(lastSent)}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      header: tableColumnTitles.sent,
      accessorKey: 'sentAmount',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => <div>{getValue() as string}x</div>,
    },
    {
      header: tableColumnTitles.bought,
      accessorKey: 'boughtAmount',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => <div>{getValue() as string}x</div>,
    },
    {
      header: tableColumnTitles.purchaseRate,
      accessorKey: 'purchaseRate',
      enableSorting: true,
      sortingFn: (a, b) => {
        const aPercent = a.original.purchaseRate
        const bPercent = b.original.purchaseRate

        return aPercent - bPercent
      },
      cell: ({ getValue }) => (
        <div>{getPercentValue(getValue() as number)}</div>
      ),
    },
    {
      header: tableColumnTitles.netRevenue,
      accessorKey: 'netRevenue',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => (
        <div>{getCurrency(getValue() as string, showCents ? 2 : 0)}</div>
      ),
    },
    {
      header: tableColumnTitles.averagePrice,
      accessorKey: 'averagePrice',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => (
        <div>{getCurrency(getValue() as string, showCents ? 2 : 0)}</div>
      ),
    },
    {
      header: () => <span className="w-full text-right">Action</span>,
      id: 'action',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleButtonClick(row.index)}
          className="float-right whitespace-nowrap p-0"
        >
          View Messages
          <RightChevronIcon className="fill-primary-500" />
        </Button>
      ),
    },
  ]

  const getTableSheetData = () => {
    return mediaSets.map((mediaSet) => ({
      [tableColumnTitles.ppvNames]: mediaSet.highlighted.content || 'No text',
      [tableColumnTitles.sent]: formatNumbersForSheet(mediaSet.sentAmount),
      [tableColumnTitles.bought]: formatNumbersForSheet(mediaSet.boughtAmount),
      [tableColumnTitles.purchaseRate]: mediaSet.purchaseRate,
      [tableColumnTitles.netRevenue]: formatNumbersForSheet(
        mediaSet.netRevenue,
      ),
      [tableColumnTitles.averagePrice]: formatNumbersForSheet(
        mediaSet.averagePrice,
      ),
    }))
  }

  return (
    <DataTable
      enablePagination
      enableSorting
      enableExport
      wrapperClassName="gap-2"
      extra={<PpvTrackingQueryPanel setSearch={setSearch} />}
      exportOptions={{
        fileName: `PPVs, ${getFormattedDateRange(date)}`,
        getTableSheetData,
      }}
      columns={columns}
      data={mediaSets}
      className="px-6 py-0"
      title="PPVs"
      titleClassName="text-3xl"
    />
  )
}
